import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Spinner, Alert, Badge, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Command Center Lead Magnets Management
 * Standalone lead magnets that can be reused across funnels
 */
function CCLeadMagnets() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leadMagnets, setLeadMagnets] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingLM, setEditingLM] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showOfferDetails, setShowOfferDetails] = useState(false);

  // Lead magnet types
  const LEAD_MAGNET_TYPES = {
    'pdf': { name: 'PDF Guide/Ebook', icon: '📄' },
    'checklist': { name: 'Checklist', icon: '✅' },
    'template': { name: 'Template/Swipe File', icon: '📋' },
    'calculator': { name: 'Calculator/Tool', icon: '🧮' },
    'assessment': { name: 'Assessment/Quiz', icon: '📊' },
    'video': { name: 'Video Training', icon: '🎥' },
    'webinar': { name: 'Webinar/Workshop', icon: '📺' },
    'course': { name: 'Mini Course', icon: '🎓' },
    'challenge': { name: 'Challenge', icon: '🏆' },
    'resource_list': { name: 'Resource List/Toolkit', icon: '🛠️' },
    'case_study': { name: 'Case Study', icon: '📈' },
    'other': { name: 'Other', icon: '📦' }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [lmRes, prodRes] = await Promise.all([
        fetch('/api/lead-magnets', { credentials: 'include' }),
        fetch('/api/lead-magnets/products', { credentials: 'include' })
      ]);

      if (lmRes.ok) {
        const lmData = await lmRes.json();
        setLeadMagnets(lmData.lead_magnets || []);
      }

      if (prodRes.ok) {
        const prodData = await prodRes.json();
        setProducts(prodData.products || []);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingLM({
      name: '',
      description: '',
      lead_magnet_type: 'pdf',
      external_url: '',
      offer_name: '',
      offer_type: 'free',
      offer_includes: '',
      product_id: ''
    });
    setShowOfferDetails(false);
    setShowModal(true);
  };

  const openEditModal = (lm) => {
    setEditingLM({ ...lm });
    setShowOfferDetails(lm.offer_name || lm.offer_includes);
    setShowModal(true);
  };

  const saveLeadMagnet = async () => {
    if (!editingLM.name?.trim()) {
      setError('Lead magnet name is required');
      return;
    }

    setSaving(true);
    try {
      const isNew = !editingLM.id;
      const url = isNew ? '/api/lead-magnets' : `/api/lead-magnets/${editingLM.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingLM)
      });

      if (response.ok) {
        setShowModal(false);
        setSuccess(isNew ? 'Lead magnet created!' : 'Lead magnet updated!');
        setTimeout(() => setSuccess(''), 3000);
        fetchData();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError('Error saving lead magnet');
    } finally {
      setSaving(false);
    }
  };

  const deleteLeadMagnet = async (lm) => {
    if (!window.confirm(`Delete "${lm.name}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`/api/lead-magnets/${lm.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Lead magnet deleted');
        setTimeout(() => setSuccess(''), 3000);
        fetchData();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Error deleting lead magnet');
    }
  };

  const getTypeBadge = (type) => {
    const typeInfo = LEAD_MAGNET_TYPES[type] || LEAD_MAGNET_TYPES['other'];
    return (
      <Badge bg="secondary" className="fw-normal">
        {typeInfo.icon} {typeInfo.name}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading lead magnets...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2"
            onClick={() => navigate('/command-center/products')}
          >
            ← Back to Products
          </Button>
          <h1 className="mb-1">Lead Magnets</h1>
          <p className="text-muted mb-0">
            Create and manage your lead magnets. These can be used across multiple funnels.
          </p>
        </div>
        <Button
          variant="dark"
          style={{ backgroundColor: colors.greyDarkest }}
          onClick={openCreateModal}
        >
          + New Lead Magnet
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Lead Magnets Table */}
      {leadMagnets.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎁</div>
            <h4>No Lead Magnets Yet</h4>
            <p className="text-muted mb-4">
              Lead magnets are free valuable content you offer in exchange for contact info.
              Create your first lead magnet to use in your funnels.
            </p>
            <Button
              variant="dark"
              style={{ backgroundColor: colors.greyDarkest }}
              onClick={openCreateModal}
            >
              Create Your First Lead Magnet
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Table responsive hover className="mb-0">
            <thead style={{ backgroundColor: colors.greyLightest }}>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Used In</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leadMagnets.map(lm => (
                <tr key={lm.id}>
                  <td>
                    <strong>{lm.name}</strong>
                    {lm.description && (
                      <div className="text-muted small" style={{ maxWidth: '300px' }}>
                        {lm.description.substring(0, 80)}{lm.description.length > 80 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td>{getTypeBadge(lm.lead_magnet_type)}</td>
                  <td>
                    {lm.funnel_count > 0 ? (
                      <Badge bg="info">{lm.funnel_count} funnel{lm.funnel_count !== 1 ? 's' : ''}</Badge>
                    ) : (
                      <span className="text-muted">Not used</span>
                    )}
                  </td>
                  <td>
                    <Badge bg={lm.status === 'active' ? 'success' : 'secondary'}>
                      {lm.status || 'active'}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Button variant="link" size="sm" onClick={() => openEditModal(lm)}>
                      Edit
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => deleteLeadMagnet(lm)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Stats */}
      {leadMagnets.length > 0 && (
        <Row className="mt-4">
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.goldMid }}>
                  {leadMagnets.length}
                </div>
                <div className="text-muted">Total Lead Magnets</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#198754' }}>
                  {leadMagnets.filter(lm => lm.status === 'active').length}
                </div>
                <div className="text-muted">Active</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0dcaf0' }}>
                  {leadMagnets.filter(lm => lm.funnel_count > 0).length}
                </div>
                <div className="text-muted">Used in Funnels</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>{editingLM?.id ? 'Edit Lead Magnet' : 'New Lead Magnet'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingLM && (
            <Form>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Name *</strong></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., '7-Step Marketing Checklist'"
                      value={editingLM.name || ''}
                      onChange={(e) => setEditingLM({ ...editingLM, name: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Type</strong></Form.Label>
                    <Form.Select
                      value={editingLM.lead_magnet_type || 'pdf'}
                      onChange={(e) => setEditingLM({ ...editingLM, lead_magnet_type: e.target.value })}
                    >
                      {Object.entries(LEAD_MAGNET_TYPES).map(([key, val]) => (
                        <option key={key} value={key}>{val.icon} {val.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label><strong>Description</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="What is this lead magnet about? What problem does it solve?"
                  value={editingLM.description || ''}
                  onChange={(e) => setEditingLM({ ...editingLM, description: e.target.value })}
                />
                <Form.Text className="text-muted">
                  This description helps AI generate better CTAs and content for this lead magnet.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>External URL</strong> (if hosted elsewhere)</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://yoursite.com/lead-magnet-landing-page"
                  value={editingLM.external_url || ''}
                  onChange={(e) => setEditingLM({ ...editingLM, external_url: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Optional: Link to an existing landing page for this lead magnet.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Leads Into Product</strong> (optional)</Form.Label>
                <Form.Select
                  value={editingLM.product_id || ''}
                  onChange={(e) => setEditingLM({ ...editingLM, product_id: e.target.value })}
                >
                  <option value="">None - standalone lead magnet</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.sale_price ? `($${p.sale_price})` : ''}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Which product does this lead magnet ultimately promote?
                </Form.Text>
              </Form.Group>

              {/* Offer Details (collapsible) */}
              <div className="border-top pt-3 mt-3">
                <Button
                  variant="link"
                  className="p-0 mb-3"
                  onClick={() => setShowOfferDetails(!showOfferDetails)}
                >
                  {showOfferDetails ? '▼' : '▶'} Offer Details (optional)
                </Button>

                {showOfferDetails && (
                  <>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Offer Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., 'Free Marketing Toolkit'"
                            value={editingLM.offer_name || ''}
                            onChange={(e) => setEditingLM({ ...editingLM, offer_name: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Offer Type</Form.Label>
                          <Form.Select
                            value={editingLM.offer_type || 'free'}
                            onChange={(e) => setEditingLM({ ...editingLM, offer_type: e.target.value })}
                          >
                            <option value="free">Free</option>
                            <option value="free_plus_shipping">Free + Shipping</option>
                            <option value="discount">Discounted</option>
                            <option value="trial">Trial</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>What's Included</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="List what they get: PDF guide, video training, templates, etc."
                        value={editingLM.offer_includes || ''}
                        onChange={(e) => setEditingLM({ ...editingLM, offer_includes: e.target.value })}
                      />
                    </Form.Group>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Regular Price</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="$97"
                            value={editingLM.offer_regular_price || ''}
                            onChange={(e) => setEditingLM({ ...editingLM, offer_regular_price: e.target.value })}
                          />
                          <Form.Text className="text-muted">For value anchoring</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Actual Price</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Free"
                            value={editingLM.offer_price || ''}
                            onChange={(e) => setEditingLM({ ...editingLM, offer_price: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Savings</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="100% off"
                            value={editingLM.offer_savings || ''}
                            onChange={(e) => setEditingLM({ ...editingLM, offer_savings: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </div>

              {editingLM.id && (
                <Form.Group className="mb-3 border-top pt-3">
                  <Form.Label><strong>Status</strong></Form.Label>
                  <Form.Select
                    value={editingLM.status || 'active'}
                    onChange={(e) => setEditingLM({ ...editingLM, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </Form.Select>
                </Form.Group>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={saveLeadMagnet}
            disabled={saving}
          >
            {saving ? <Spinner animation="border" size="sm" /> : (editingLM?.id ? 'Save Changes' : 'Create Lead Magnet')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CCLeadMagnets;
